// tosContentInterno.js
//
// Variante **Tipo C (interno/a de medicina en formación)** de los Términos de
// Uso click-through. La ve quien tiene `profession === "INTERNO"`; el resto ve
// el Tipo A (tosContent.js). Fuente original:
// proyectos_extra/operacional/docs_legales/terminos_uso_clickthrough_interno.md.
//
// Criterio de fondo (Sebastián, 10-ago-2026): el interno ASUME la
// responsabilidad de las notas que redacta y firma con su cargo "Int.", aunque
// NO sea el médico tratante. La variante corrige lo que en el Tipo A sería falso
// para un interno (habilitación, calidad de tratante, contrato y pago) y fija el
// acceso como la MISMA prueba gratuita de #1133 que cualquier otra cuenta.
//
// La sección 3 (datos personales) es la misma del Tipo A (tosDatosPersonales.js),
// alineada con la Política de Privacidad mínima v2.0 (Planner 347, 05-oct-2026).
// Cambiar este texto exige subir TOS_VERSION_BY_PROFESSION["INTERNO"] en el backend.

import { PRIVACIDAD_EMAIL, POLITICA_URL, seccionDatosPersonales } from "./tosDatosPersonales";
import { conComunicaciones, VERSIONES_V1 } from "./tosContent";
import { getTosInternoMarkdownV1 } from "./tosVersionesAnteriores";

export const TOS_CONTACT_EMAIL = PRIVACIDAD_EMAIL;
export const ARCOP_CONTACT_EMAIL = PRIVACIDAD_EMAIL;

const seccion3 = seccionDatosPersonales({
  rolPacientes:
    "- **Información de los pacientes:** tú decides qué información ingresas a la Plataforma y para\n" +
    "  qué, y respondes por ese ingreso. SmartDx (Salud Wild SpA) actúa como **Encargado**: presta el\n" +
    "  servicio según tus instrucciones.",
  extra: [
    "- Declaras que actúas **dentro de los permisos y las instrucciones de la institución** en la que\n" +
      "  realizas tu internado. Cuando esa institución sea el Responsable del tratamiento de los datos\n" +
      "  de sus pacientes, declaras contar con su autorización para usar la Plataforma con esa\n" +
      "  información y te comprometes a suspender su uso si dicha autorización cesa.",
  ],
  cooperacionPaciente:
    "- Si un paciente quiere ejercer sus derechos sobre información clínica tratada mediante\n" +
    "  SmartDx, debe dirigirse en primer término al profesional o a la institución tratante, que es\n" +
    "  el Responsable de esos datos. SmartDx coopera con ellos para atenderla dentro de los plazos\n" +
    "  legales.",
});

const getTosInternoMarkdownBase = (
  version = ""
) => `# Términos de Uso de la Plataforma SmartDx
### Modalidad interno/a de medicina en formación (Tipo C)

**Emisor:** Salud Wild SpA, RUT 78.149.483-6 ("SmartDx", "nosotros").
**Versión:** ${version || "vigente"} · **Vigencia:** desde tu aceptación.

Al marcar la casilla **"He leído y acepto los Términos de Uso"** y activar tu cuenta, aceptas
estos Términos. Estos Términos y la [Política de Privacidad](${POLITICA_URL}) regulan tu
uso de la Plataforma SmartDx como interno/a de medicina.

---

## 1. Qué es SmartDx (y qué no es)

SmartDx es una **herramienta de apoyo a la documentación clínica**, asistida con inteligencia
artificial. Te ayuda a redactar, estructurar, editar y visualizar borradores de documentación
clínica (por defecto, del **Modo Turno**: evoluciones, ingresos, pendientes, indicaciones y
resúmenes por paciente de tu ronda o turno).

**SmartDx NO es un dispositivo médico y no reemplaza tu juicio clínico.** No emite diagnósticos,
indicaciones terapéuticas, alertas, priorizaciones ni recomendaciones clínicas autónomas. Todo
lo que genera es un **borrador preliminar y editable**: tú lo revisas, corriges, validas y, en
su caso, firmas antes de usarlo, comunicarlo o incorporarlo a la ficha clínica. La decisión
clínica final es siempre tuya.

## 2. Tu responsabilidad como interno/a de medicina

Al usar SmartDx declaras y aceptas que:

- Eres **estudiante de medicina cursando el internado** en una institución de salud, y usas la
  Plataforma **exclusivamente** en el marco de tu práctica clínica asignada, respecto de
  pacientes a cuya atención concurres y a cuya información accedes de forma autorizada.
- **No declaras ser un profesional habilitado ni el médico tratante.** Firmas los documentos con
  tu cargo de interno/a ("Int." seguido de tu apellido) y no con el de un médico titulado.
- **Asumes la responsabilidad del contenido que redactas, revisas y firmas.** Toda revisión,
  validación, corrección y firma de cualquier borrador generado con SmartDx, así como su
  exactitud y su uso o incorporación a la ficha clínica, son de **tu responsabilidad**
  académica, ética y, en lo que corresponda a tu calidad de interno/a, legal. El hecho de que
  SmartDx haya generado un borrador no traslada esa responsabilidad a SmartDx.
- Reconoces que, por tu condición de interno/a, los documentos que produzcas quedan sujetos a la
  **supervisión, validación y contrafirma** que exija tu centro formador o asistencial. Cumplir
  con esas reglas es tu obligación; **no las reemplaza ni las suple el uso de SmartDx**.
- Cuentas con los **perfiles de acceso institucionales** y las autorizaciones necesarias para
  acceder y tratar la información de los pacientes con la que trabajas en la Plataforma
  (incluidos datos sensibles de salud), y usarás únicamente la información a la que tu
  institución te haya dado acceso legítimo.
- La incorporación de los documentos definitivos a la ficha clínica y la custodia de la ficha
  corresponden a tu institución (o a quien corresponda). SmartDx no reemplaza la ficha clínica:
  la información que tratas en el Modo Turno se conserva por los plazos que indica la Política
  de Privacidad.

${seccion3}

## 4. Licencia de uso

Te otorgamos una licencia **limitada, temporal, no exclusiva, intransferible, no sublicenciable
y revocable** para usar la Plataforma durante la vigencia del acceso y sólo para tu práctica
clínica formativa. La propiedad intelectual de SmartDx (software, modelos, interfaces, marca) es
y seguirá siendo de Salud Wild SpA. Los **outputs** que generes a partir de tu información son
tuyos (o de tu institución).

Te comprometes a **no** compartir tus credenciales (son personales e intransferibles), ni hacer
ingeniería reversa, copia, extracción masiva, scraping, reventa o cualquier uso no autorizado de
la Plataforma, y a avisarnos ante cualquier falla, acceso no autorizado o incidente que
detectes.

## 5. Período de prueba gratuito y eventual paso a modalidad pagada

Tu acceso se otorga en modalidad de formación y **sin contrato de servicios asociado**, sin
obligación de pago alguna.

Al crear tu cuenta puedes usar la Plataforma de inmediato, sin trámites previos. El plazo de tu
prueba gratuita (**14 días**) comienza a correr con el **primer documento clínico que generes**
con SmartDx (tu primera evolución, ingreso o resumen). Si no generas ningún documento, la prueba
igual comienza a los **30 días** de creada tu cuenta. Subir tu carnet de identidad **no alarga**
este plazo: es un trámite aparte, para que la revisión humana de tu cuenta pueda completarse.

Además te vamos a pedir tu carnet de identidad, para que la revisión humana de tu cuenta pueda
completarse. Es un trámite aparte: no tiene plazo propio y no subirlo no suspende tu acceso
durante la prueba.

Durante toda la prueba no tienes obligación de pago alguna, ni SmartDx asume compromiso de
permanencia, disponibilidad o soporte.

**Al vencer el plazo vigente, el acceso puede terminar.** Si ambas partes quieren continuar,
SmartDx podrá ofrecerte seguir usando la Plataforma en **modalidad pagada**, cuyo precio y
condiciones se regirán por el Contrato SaaS o la orden de contratación que aceptes en ese
momento, y por los Términos que correspondan a esa modalidad.

**No hay renovación automática ni cobro sin tu consentimiento expreso**: no se te cobrará nada
por el uso de la Plataforma mientras no aceptes de forma explícita las condiciones económicas de
la modalidad pagada.

Podemos **limitar, suspender o revocar** el acceso en cualquier momento, también antes del
vencimiento del plazo vigente, en particular si termina tu internado, si cesa la autorización de
tu institución o si haces un uso contrario a estos Términos.

## 6. Disponibilidad y responsabilidad

- Hacemos esfuerzos comercialmente razonables por mantener la Plataforma disponible, pero puede
  haber interrupciones (mantenciones, fallas de terceros, fuerza mayor). No garantizamos
  operación ininterrumpida ni libre de errores.
- SmartDx **no responde** por decisiones clínicas ni por errores derivados de la información que
  tú o terceros ingresen. No respondemos por daños indirectos, lucro cesante ni perjuicios
  consecuenciales.
- Tratándose de un acceso sin costo, **nuestra responsabilidad total acumulada se limita al
  máximo permitido por la ley aplicable**.

## 7. Vigencia y término

Estos Términos rigen mientras uses la Plataforma. El acceso termina **al vencer tu prueba
gratuita** (salvo que acuerdes continuar en modalidad pagada) y, en todo caso, al concluir tu
internado o la autorización institucional que lo sustenta. Cualquiera de las partes puede
ponerle término en cualquier momento, sin expresión de causa y sin indemnización alguna.

## 8. Ley aplicable y jurisdicción

Estos Términos se rigen por la ley chilena. Cualquier controversia se somete a los Tribunales
Ordinarios de Justicia de Santiago de Chile.

## 9. Aceptación

Al marcar **"He leído y acepto los Términos de Uso"** manifiestas tu consentimiento mediante
firma electrónica simple (Ley N° 19.799). Dejaremos registro de tu identidad de usuario, fecha y
hora de aceptación y versión aceptada de estos Términos.

Si cambiamos estos Términos de forma sustancial, te avisaremos por correo electrónico o dentro de
la plataforma y te pediremos aceptar la nueva versión para seguir usándola.

Contacto: ${TOS_CONTACT_EMAIL} · Salud Wild SpA · Antonio Bellet 193, of. 1210, Providencia,
Santiago.

---

> **Recuerda:** SmartDx es un asistente de redacción clínica. Los borradores son preliminares y
> editables, y **no reemplazan tu juicio clínico**. Firmas como **Int. \\<Apellido\\>** y respondes
> por lo que firmas, sin perjuicio de la supervisión que exija tu centro.
`;

/** Términos Tipo C: una v1.x, su texto congelado (#1683 R47); cualquier otra, el texto nuevo (+ 3 bis si está en el set, R46). */
export const getTosInternoMarkdown = (version = "") =>
  VERSIONES_V1.has(version)
    ? getTosInternoMarkdownV1(version)
    : conComunicaciones(getTosInternoMarkdownBase(version), version);

/** Solo para el test de R46: el texto v2.0-interno SIN la 3 bis. */
export const getTosInternoMarkdownSinComunicaciones = getTosInternoMarkdownBase;

export default getTosInternoMarkdown;
